// useProjectConfig.js
// Resolves the correct project config at runtime based on hostname and path.
//
// Resolution order:
//   1. datamvps.tripleco.com -> always TCC Data Platform (static import)
//   2. tk.tripleco.com/[id]  -> fetch project by id from API
//   3. [subdomain].tripleco.com -> fetch project matching that domain from API
//   4. fallback -> TCC Data Platform
//
// Returns the same shape as projectConfig.js so App.js needs minimal changes.

import { useState, useEffect } from "react";
import {
  NAV as TCC_NAV,
  PROJECT_META as TCC_META,
  PROJECT_PERMISSIONS as TCC_PERMISSIONS,
  SUMMIT_SESSIONS as TCC_SUMMIT_SESSIONS,
  TASKS as TCC_TASKS,
  GUIDING_PRINCIPLES as TCC_GUIDING_PRINCIPLES,
  SUCCESS_TARGETS as TCC_SUCCESS_TARGETS,
  CHANGE_MGMT_STEPS as TCC_CHANGE_MGMT_STEPS,
  RESISTANCE_PATTERNS as TCC_RESISTANCE_PATTERNS,
  DRAFT_MATERIALS_BASE as TCC_DRAFT_MATERIALS,
} from "./projectConfig";

// Build a project config from an API project JSON
export function buildConfigFromProject(project) {
  const tabs = project.tabs || {};
  const allTabs = [
    ...(tabs.public || []),
    ...(tabs.authenticated || []),
    ...(tabs.admin || []),
  ];

  // Build NAV from project tabs - preserve order from ALL_TABS reference
  const NAV = allTabs;

  // Build PROJECT_META from project JSON
  const PROJECT_META = {
    name: project.name || "Project",
    client: project.client || "",
    subtitle: project.subtitle || "",
    domain: project.domain || "",
    sharepoint: project.sharepoint || {},
    azure: project.azure || {},
  };

  // Build PROJECT_PERMISSIONS from project JSON
  const PROJECT_PERMISSIONS = project.permissions || {
    groups: {},
    userOverrides: {},
    defaultLevel: "write",
    projectId: project.id,
    clientId: project.client?.toLowerCase().replace(/[^a-z0-9]/g, "") || "",
  };
  // Inject projectId and clientId if not present
  if (!PROJECT_PERMISSIONS.projectId) PROJECT_PERMISSIONS.projectId = project.id;

  // Use the project's own summit sessions/tasks when it defines them;
  // fall back to TCC's hardcoded content only when it doesn't. Before this,
  // every project silently got TCC's SUMMIT_SESSIONS/TASKS baked in
  // regardless -- harmless only by coincidence, since the one project that
  // existed was TCC's own.
  const SUMMIT_SESSIONS = project.summitSessions || TCC_SUMMIT_SESSIONS;
  const TASKS = project.tasks || TCC_TASKS;

  return {
    NAV,
    PROJECT_META,
    PROJECT_PERMISSIONS,
    SUMMIT_SESSIONS,
    TASKS,
    GUIDING_PRINCIPLES: TCC_GUIDING_PRINCIPLES,
    SUCCESS_TARGETS: TCC_SUCCESS_TARGETS,
    CHANGE_MGMT_STEPS: TCC_CHANGE_MGMT_STEPS,
    RESISTANCE_PATTERNS: TCC_RESISTANCE_PATTERNS,
    DRAFT_MATERIALS_BASE: TCC_DRAFT_MATERIALS,
    projectId: project.id,
    isLoaded: true,
    isTCC: false,
  };
}

// TCC static config
const TCC_CONFIG = {
  NAV: TCC_NAV,
  PROJECT_META: TCC_META,
  PROJECT_PERMISSIONS: TCC_PERMISSIONS,
  SUMMIT_SESSIONS: TCC_SUMMIT_SESSIONS,
  TASKS: TCC_TASKS,
  GUIDING_PRINCIPLES: TCC_GUIDING_PRINCIPLES,
  SUCCESS_TARGETS: TCC_SUCCESS_TARGETS,
  CHANGE_MGMT_STEPS: TCC_CHANGE_MGMT_STEPS,
  RESISTANCE_PATTERNS: TCC_RESISTANCE_PATTERNS,
  DRAFT_MATERIALS_BASE: TCC_DRAFT_MATERIALS,
  projectId: "tcc-data-platform",
  isLoaded: true,
  isTCC: true,
};

export default function useProjectConfig() {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  // datamvps always returns TCC config immediately - no API call needed
  const isDatamvps = hostname === "datamvps.tripleco.com" || hostname === "localhost";
  const isTkPortal = hostname === "tk.tripleco.com" || hostname === "tk.localhost";

  // Extract project ID from path on tk (e.g. /john -> "john")
  const pathId = isTkPortal && pathname !== "/" && pathname !== ""
    ? pathname.replace(/^\/+/, "").split("/")[0].toLowerCase()
    : null;

  // Extract subdomain (e.g. john.tripleco.com -> "john")
  const subdomain = !isDatamvps && !isTkPortal && hostname.endsWith(".tripleco.com")
    ? hostname.replace(".tripleco.com", "")
    : null;

  const [config, setConfig] = useState(isDatamvps ? TCC_CONFIG : null);
  const [loading, setLoading] = useState(!isDatamvps);
  const [error, setError] = useState(null);

  useEffect(function() {
    // datamvps uses static TCC config - never fetch
    if (isDatamvps) return;

    // tk.tripleco.com root (no path) - no project config needed, ProjectSelector handles it
    if (isTkPortal && !pathId) {
      setLoading(false);
      return;
    }

    // /docs is a reserved route (DocsSection), not a project id - no fetch
    if (isTkPortal && pathId === "docs") {
      setLoading(false);
      return;
    }

    // Determine what to fetch
    const projectIdToFetch = pathId || subdomain;
    if (!projectIdToFetch) {
      setConfig(TCC_CONFIG);
      setLoading(false);
      return;
    }

    // Try fetching by project ID first
    fetch("/api/projects/" + projectIdToFetch)
      .then(function(r) {
        if (!r.ok) throw new Error("Project not found: " + projectIdToFetch);
        return r.json();
      })
      .then(function(project) {
        if (project.status === "archived") throw new Error("Project archived");
        setConfig(buildConfigFromProject(project));
        setLoading(false);
      })
      .catch(function(e) {
        // If not found by ID, try finding by domain
        fetch("/api/projects")
          .then(function(r) { return r.json(); })
          .then(function(data) {
            const match = (data.projects || []).find(function(p) {
              return p.domain && p.domain.includes(subdomain || projectIdToFetch);
            });
            if (match) {
              return fetch("/api/projects/" + match.id).then(function(r) { return r.json(); });
            }
            throw new Error("No matching project");
          })
          .then(function(project) {
            setConfig(buildConfigFromProject(project));
            setLoading(false);
          })
          .catch(function() {
            // Final fallback - show project not found
            setError("Project not found: " + projectIdToFetch);
            setLoading(false);
          });
      });
  }, [isDatamvps, isTkPortal, pathId, subdomain]);

  return { config, loading, error, isTkPortal, pathId };
}
